import { Send } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { getAspectRatioValue, getObjectPosition } from "../lib/mediaCrop";
import type { ButtonLayout, CardButton, CropPosition, MediaAspectRatio, MediaType } from "../lib/types";

interface CardPreviewProps {
  mediaType: MediaType;
  file: File | null;
  title: string;
  caption: string;
  buttons: CardButton[];
  layout: ButtonLayout;
  aspectRatio: MediaAspectRatio;
  cropPosition: CropPosition;
}

function chunkButtons(buttons: CardButton[], layout: ButtonLayout) {
  const cleanButtons = buttons.filter((button) => button.text.trim() && button.url.trim());
  const rows: CardButton[][] = [];
  for (let index = 0; index < cleanButtons.length; index += layout) {
    rows.push(cleanButtons.slice(index, index + layout));
  }
  return rows;
}

function getNaturalMediaRatio(file: File, url: string, mediaType: MediaType) {
  return new Promise<number | null>((resolve) => {
    if (mediaType === "video") {
      const video = document.createElement("video");
      video.onloadedmetadata = () => resolve(video.videoWidth && video.videoHeight ? video.videoWidth / video.videoHeight : null);
      video.onerror = () => resolve(null);
      video.src = url;
      return;
    }

    const image = new Image();
    image.onload = () => resolve(image.naturalWidth && image.naturalHeight ? image.naturalWidth / image.naturalHeight : null);
    image.onerror = () => resolve(null);
    image.src = url;
  });
}

export function CardPreview({
  mediaType,
  file,
  title,
  caption,
  buttons,
  layout,
  aspectRatio,
  cropPosition
}: CardPreviewProps) {
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [naturalRatio, setNaturalRatio] = useState<number | null>(null);
  const rows = useMemo(() => chunkButtons(buttons, layout), [buttons, layout]);
  const selectedRatio = getAspectRatioValue(aspectRatio);
  const mediaRatio = selectedRatio ?? naturalRatio ?? 16 / 9;
  const objectPosition = getObjectPosition(cropPosition);

  useEffect(() => {
    if (!file) {
      setPreviewUrl(null);
      setNaturalRatio(null);
      return;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    setNaturalRatio(null);
    getNaturalMediaRatio(file, url, mediaType).then(setNaturalRatio);
    return () => URL.revokeObjectURL(url);
  }, [file, mediaType]);

  return (
    <aside className="sticky top-4">
      <div className="rounded border border-line bg-[#dce8ef] p-3 shadow-soft">
        <div className="mb-3 flex items-center gap-2 border-b border-slate-300 pb-2">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand text-sm font-bold text-white">TC</div>
          <div className="min-w-0">
            <div className="truncate text-sm font-semibold text-ink">Telegram Preview</div>
            <div className="text-xs text-slate-600">
              {aspectRatio === "original" ? "Original ratio" : `${aspectRatio} cover / ${cropPosition}`}
            </div>
          </div>
        </div>

        <div className="ml-auto max-w-[420px] overflow-hidden rounded bg-white shadow">
          {previewUrl ? (
            <div className="w-full bg-slate-100" style={{ aspectRatio: mediaRatio }}>
              {mediaType === "video" ? (
                <video
                  src={previewUrl}
                  className="h-full w-full object-cover"
                  style={{ objectPosition }}
                  controls
                  muted
                />
              ) : (
                <img
                  src={previewUrl}
                  className="h-full w-full object-cover"
                  style={{ objectPosition }}
                  alt="Card media preview"
                />
              )}
            </div>
          ) : (
            <div className="flex aspect-video items-center justify-center bg-slate-100 text-sm text-slate-500">Media preview</div>
          )}

          <div className="whitespace-pre-wrap break-words px-3 py-3 text-sm leading-6 text-ink">
            {title.trim() ? <div className="mb-1 font-semibold">{title}</div> : null}
            {caption.trim() || "เขียน caption แล้ว preview จะแสดงตรงนี้"}
          </div>

          {rows.length ? (
            <div className="space-y-1 border-t border-slate-100 px-2 pb-2 pt-2">
              {rows.map((row, rowIndex) => (
                <div key={rowIndex} className="grid gap-1" style={{ gridTemplateColumns: `repeat(${row.length}, minmax(0, 1fr))` }}>
                  {row.map((button) => (
                    <div
                      key={button.id}
                      className="flex min-h-9 items-center justify-center gap-1 rounded border border-[#c7d8e6] bg-[#eef7ff] px-2 text-center text-sm font-medium text-[#168acd]"
                    >
                      <Send size={13} />
                      <span className="truncate">{button.text}</span>
                    </div>
                  ))}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </aside>
  );
}
